import * as d3 from 'd3'
import { DateTime, Duration } from 'luxon'
import { Component, createRef } from 'react'

import { hosPlan } from '../../api/schedules'

const SCHEDULE_SWIMLANES = ['ORIGIN', 'STOP', 'DESTINATION', 'TERMINAL', 'END']
const DRIVING_STATES = ['OFF_DUTY', 'ON_DUTY', 'DRIVING']

// Create a JS Date from a timestamp (ISO format) and a timezone
const getDate = (date: any, timezone: any) => {
  var formattedDate = DateTime.fromISO(date).setZone(timezone)
  return formattedDate.toLocal().toJSDate()
}

// Get the unified stop type - delegates to load_stop if this event has a load stop, otherwise uses the event type.
const unifiedStopType = (event: any) => {
  if (event.type === 'LOAD') {
    return event.load_stop.type
  }
  return event.type
}

// Get the starting time of the appointment stop – basically, the event end minus the dwell time, which represents when
// a driver arrived at a stop.
const getEventStopStart = (event: any) => {
  let end_date = DateTime.fromISO(event.date_end).setZone(event.location_timezone)
  let start = end_date.minus({ hours: event.dwell_hours })

  return start.toLocal().toJSDate()
}

// Get the length of time an appointment lasts (in a scale range), with an assumed appointment time of 2 hours
// if the appointment has no provided duration.
const getAppointmentDuration = (event: any, scale: any) => {
  let start_date = DateTime.fromISO(event.load_stop.date_start)
    .setZone(event.load_stop.location_timezone)
    .toLocal()
  let start = scale(start_date.toJSDate())
  let end = scale(getDate(event.load_stop.date_end, event.load_stop.location_timezone))

  let duration = end - start
  if (duration <= 0) {
    let end = scale(start_date.plus({ hours: 2 }).toLocal().toJSDate())
    duration = end - start
  }
  return duration
}

// String representation of the load in human-ish form.
const getLoadID = (event: any) => {
  if (event.load === null) {
    return ''
  }
  return event.load.source + ' ' + event.load.source_id
}

// Return a unique list, with duplicates removed.
const unique = (items: any) => {
  var uniq: any = {}
  items.forEach((item: any) => {
    uniq[item] = true
  })
  return Object.keys(uniq)
}

class ScheduleCalendar extends Component {
  d3canvas: any
  props: any
  constructor(props: any) {
    super(props)
    this.d3canvas = createRef()
  }
  size = { width: 600, height: 1200 }
  componentDidMount() {
    let size = this.size
    let margin = { left: 50, right: 20, top: 75, bottom: 20 }
    let svg = d3
      .select(this.d3canvas.current)
      .append('svg')
      .attr('width', size.width)
      .attr('height', size.height)
      // outline is helpful to debug figure positioning
      //.attr("style", "outline: thin solid red;")
      .attr('transform', 'translate(300,-300)rotate(-90, 0, 0)')

    let events = this.props.record.events

    // Start getting the HOS plan from the backend.
    let plan_promise = hosPlan(this.props.record)
    let start = new Date(
      d3.min(events.map((event: any): Date => getDate(event.date_start, event.location_timezone))) || ''
    )

    // Axes scales for appointment graphs
    let y: any = d3
      .scaleTime()
      .domain([
        start,
        new Date(d3.max(events.map((event: any) => getDate(event.date_end, event.location_timezone))) || ''),
      ])
      .range([margin.top, size.height - margin.bottom])
    let x: any = d3
      .scaleBand()
      .domain(SCHEDULE_SWIMLANES)
      .range([margin.left, 250 - margin.right])
    let c: any = d3
      .scaleOrdinal()
      .domain(unique(events.map(getLoadID)))
      .range(d3.schemeCategory10)

    // Appointment Times
    svg
      .append('g')
      .selectAll('rect')
      .data(events.filter((event: any) => event.load_stop != null))
      .join('rect')
      .attr('x', (d: any): any => x(unifiedStopType(d)))
      .attr('width', x.bandwidth())
      .attr('y', (d: any) => y(getDate(d.load_stop.date_start, d.load_stop.location_timezone)))
      .attr('height', (d: any) => getAppointmentDuration(d, y))
      .attr('fill', (d: any): any => {
        let color: any = d3.color(c(getLoadID(d)))
        color.opacity = 0.4
        return color
      })

    // Driving
    svg
      .append('g')
      .selectAll('line')
      .data(events)
      .join('line')
      .attr('x1', (d: any) => x(unifiedStopType(d)) + x.bandwidth() / 2)
      .attr('x2', (d: any) => x(unifiedStopType(d)) + x.bandwidth() / 2)
      .attr('y1', (d: any) => y(getDate(d.date_start, d.location_timezone)))
      .attr('y2', (d: any) => y(getDate(d.date_end, d.location_timezone)))
      .attr('stroke', (d: any): any => {
        if (unifiedStopType(d) === 'DESTINATION' || unifiedStopType(d) === 'STOP') {
          let color: any = d3.color(c(getLoadID(d)))
          color.opacity = 1.0
          return color
        }
        return d3.color('grey')
      })
      .attr('stroke-width', 2)

    // Actual appointment moments
    svg
      .append('g')
      .selectAll('rect')
      .data(events)
      .join('rect')
      .attr('x', (d: any) => x(unifiedStopType(d)) + x.bandwidth() / 4)
      .attr('width', x.bandwidth() / 2)
      .attr('y', (d: any) => y(getEventStopStart(d)))
      .attr('height', (d: any) => y(getDate(d.date_end, d.location_timezone)) - y(getEventStopStart(d)))
      .attr('fill', (d: any): any => {
        let color: any = d3.color(c(getLoadID(d)))
        color.opacity = 1.0
        return color
      })

    // Jobs
    svg
      .append('g')
      .selectAll('rect')
      .data(events)
      .join('rect')
      .attr('fill', (d: any): any => {
        let color: any = d3.color(c(getLoadID(d)))
        color.opacity = 0.1
        return color
      })
      .attr('x', margin.left)
      .attr('width', 250 - margin.left - margin.right)
      .attr('y', (d: any) => y(getDate(d.date_start, d.location_timezone)))
      .attr(
        'height',
        (d: any) =>
          y(getDate(d.date_end, d.location_timezone)) - y(getDate(d.date_start, d.location_timezone))
      )

    // Job Labels
    svg
      .append('g')
      .attr('fill', 'black')
      .attr('text-anchor', 'start')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 12)
      .selectAll('text')
      .data(events.filter((event: any) => event.load_stop != null))
      .join('text')
      .call((text: any) =>
        text
          .filter((event: any) => event.load_stop.type === 'ORIGIN')
          .attr('x', x('END'))
          .attr('y', (d: any) => y(getDate(d.date_start, d.location_timezone)))
          .attr('transform', (d: any) => {
            let yp = y(getDate(d.date_start, d.location_timezone))
            return `rotate(90, ${x('END')}, ${yp})`
          })
          .attr('dx', '0.5em')
          .attr('dy', '-2em')
          .text(getLoadID)
      )

    // Anchor Labels
    svg
      .append('g')
      .attr('fill', 'black')
      .attr('text-anchor', 'start')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 12)
      .selectAll('text')
      .data(events.filter((event: any) => event.load_stop == null))
      .join('text')
      .call((text: any) =>
        text
          .attr('x', x('END'))
          .attr('y', (d: any) => y(getDate(d.date_start, d.location_timezone)))
          .attr('transform', (d: any) => {
            let yp = y(getDate(d.date_start, d.location_timezone))
            return `rotate(90, ${x('END')}, ${yp})`
          })
          .attr('dx', '0.5em')
          .attr('dy', '-2em')
          .text((event: any) => {
            if (event.type === 'START') {
              return 'Start'
            } else if (event.type === 'END') {
              return 'Drive to End'
            } else {
              return event.type
            }
          })
      )

    // Axes
    svg
      .append('g')
      .call((g: any) => g.attr('transform', `translate(${margin.left}, 0)`))
      .call(d3.axisLeft(y))
    svg
      .append('g')
      .call((g: any) => g.attr('transform', `translate(0, ${margin.top})`))
      .call(d3.axisTop(x))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '1.15em')
      .attr('transform', 'rotate(90)')

    // HOS Plan
    margin = { left: 300, right: 20, top: 75, bottom: 20 }

    // Replace the x scale to make a second figure
    x = d3
      .scaleBand()
      .domain(DRIVING_STATES)
      .range([margin.left, 500 - margin.right])
      .padding(0.5)
    let driving_color = d3.scaleOrdinal().domain(DRIVING_STATES).range(['grey', 'gold', 'green'])

    // Add axes
    svg
      .append('g')
      .call((g: any) => g.attr('transform', `translate(${margin.left}, 0)`))
      .call(d3.axisLeft(y))
    svg
      .append('g')
      .call((g: any) => g.attr('transform', `translate(0, ${margin.top})`))
      .call(d3.axisTop(x))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '1.15em')
      .attr('transform', 'rotate(90)')

    // Put the actual HOS plan in when we get it from the backend.
    plan_promise.then((plan: any) => {
      // HOS Log Blocks

      let plan_start = DateTime.fromJSDate(start).setZone('local')
      let plan_infos = plan.data.events.map((event: any) => {
        let plan_start_here = plan_start.toJSDate()
        plan_start = plan_start.plus(Duration.fromObject({ hours: event.duration }))
        let info = {
          state: event.state.slice(13),
          start: plan_start_here,
          end: plan_start.toLocal().toJSDate(),
        }
        return info
      })

      svg
        .append('g')
        .selectAll('rect')
        .data(plan_infos)
        .join('rect')
        .attr('x', (d: any) => x(d.state))
        .attr('width', x.bandwidth())
        .attr('y', (d: any) => y(d.start))
        .attr('height', (d: any) => y(d.end) - y(d.start))
        .attr('fill', (d: any): any => driving_color(d.state))
    })
  }
  render() {
    return <div ref={this.d3canvas} style={{ width: this.size.height, height: this.size.width }}></div>
  }
}

export default ScheduleCalendar
